/*
  Set names as a visitor reads them (docs/specs/pop-language-split.md D10, D11).

  The catalogue's names carry two kinds of debris at the end:

    - a spreadsheet's boolean, `FALSE` or `(FALSE)`, glued on by an old import — 8 Pokémon
      rows, e.g. "S-P Promotional Cards(FALSE)";
    - a language suffix, `(JPN Release)`, `(Japanese)`, `(English)`, `(Chinese)` or
      `(Korean)`, which the language toggle now says for them.

  Both come off for DISPLAY ONLY (D10). The route is `/:gameCode/:setName` and the set page
  looks the set up by name, so every link keeps the raw name: cleaning it there would send
  a visitor to a set that does not exist.

  Cleaning can make two different sets read the same — "S&M: Double Blaze" (Japanese) and
  "S&M: Double Blaze (JPN Release)FALSE" (no language, so also Japanese, D3) both clean to
  "S&M: Double Blaze" and open different pages. `displayNames` catches that and gives both
  their name with only the `FALSE` removed (D11), so the two stay told apart.
*/

const TRAILING_FALSE = /\s*(?:\(\s*FALSE\s*\)|FALSE)\s*$/;
const LANGUAGE_SUFFIX = /\s*\((?:JPN Release|Japanese|English|Chinese|Korean)\)\s*$/i;

const tidy = (name: string): string => name.replace(/\s+/g, " ").trim();

/* Strips `pattern` off the end until nothing more comes off, so suffixes stack in any order. */
const stripTrailing = (raw: string, patterns: readonly RegExp[]): string => {
  let name = raw;
  let previous: string;
  do {
    previous = name;
    name = patterns.reduce((current, pattern) => current.replace(pattern, ""), previous);
  } while (name !== previous);
  /* Never an empty name: a set called only "(Japanese)" would be a link with no text. */
  return tidy(name) || tidy(raw);
};

/* Only the `FALSE` debris (D11's fallback). */
export const stripFalse = (raw: string): string => stripTrailing(raw, [TRAILING_FALSE]);

/* D10: the `FALSE` debris and every trailing language suffix, in any combination. */
export const cleanSetName = (raw: string): string =>
  stripTrailing(raw, [TRAILING_FALSE, LANGUAGE_SUFFIX]);

/*
  Raw trimmed name -> display name, over one bucket's sets. Keyed on the trimmed name
  because that is combineSetsByName's key: one combined row, one display name.

  D11 is applied to every group of DIFFERENT raw names that clean alike. If even
  `stripFalse` leaves two of them identical (which only a name differing by nothing but
  `FALSE` could do), those fall back to the trimmed raw name: a visitor must never see one
  name on two links to two pages.
*/
export const displayNames = (
  sets: readonly { setName: string; language?: string | null }[],
  /*
    In the Other bucket a set with no recorded language has no chip, so its name is the
    only place that says "Korean": keep the language suffix there (D12's reasoning).
  */
  keepLanguageSuffix: (set: { setName: string; language?: string | null }) => boolean = () => false
): Map<string, string> => {
  const raws = Array.from(new Set(sets.map((set) => set.setName.trim())));
  const keep = new Set(sets.filter(keepLanguageSuffix).map((set) => set.setName.trim()));

  const byClean = new Map<string, string[]>();
  raws.forEach((raw) => {
    const clean = keep.has(raw) ? stripFalse(raw) : cleanSetName(raw);
    byClean.set(clean, [...(byClean.get(clean) ?? []), raw]);
  });

  const names = new Map<string, string>();
  byClean.forEach((group, clean) => {
    group.forEach((raw) => {
      names.set(raw, group.length > 1 ? stripFalse(raw) : clean);
    });
  });

  const uses = new Map<string, number>();
  names.forEach((display) => uses.set(display, (uses.get(display) ?? 0) + 1));
  names.forEach((display, raw) => {
    if ((uses.get(display) ?? 0) > 1) {
      names.set(raw, tidy(raw));
    }
  });

  return names;
};
